import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_ENDONYMS, supportedLngs } from "@heraion/i18n";
import { changeEditLocale, type Draft, type Locale } from "@heraion/shared";

import { Chip } from "../primitives/Chip";
import { ChoiceTray } from "../primitives/ChoiceTray";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";

// PE-21 slice B — the language the program editor is WRITING.
//
// Same shape as the gym chip (`primitives/GymChip.tsx`): a small chip that
// states the current answer, and opens a <ChoiceTray> in place. Chosen
// over SettingsPillChoice deliberately — that one is an always-visible
// Settings card, and Antenore's rule here is that whoever doesn't
// translate must not find this in their way.
//
// ⭐ TWO QUESTIONS, ONE CONTROL. A program written before PE-21 never
// recorded the language of its text, and we refuse to guess it (guessing
// would file Italian prose under `fr` the moment a French reader touched
// it). So when `value` is null the tray asks which language the program
// IS written in, and the answer DECLARES it. Afterwards the same tray
// asks which language you are writing, and the answer SWITCHES. The
// caller gets the two apart, because one of them rewrites the draft and
// the other only labels it.
//
// ⭐ PE-32 — the DRAFT WIRING lives in here, not in the callsites. It used to
// arrive as `value`/`onDeclare`/`onSwitch`, and the moment the prescribe tree
// needed the same chip that would have been the second copy of the same four
// lines — the one place a "declare" could quietly drift into a "switch" and
// file Italian prose under `fr`. Both callsites hold the same
// `Draft`/`setDraft` pair, so the hook takes that pair instead.
export type LanguageChipProps = {
  theme: UiTheme;
  /** The program draft — `editLocale` is the language being written, `null`
   *  when the program never declared one (PE-21 §6 nodo 3). */
  draft: Draft;
  setDraft: Dispatch<SetStateAction<Draft>>;
};

export function useLanguageChip({
  theme,
  draft,
  setDraft,
}: LanguageChipProps): { trigger: ReactNode; tray: ReactNode } {
  const { t } = useTranslation();
  const disclosure = useDisclosure(false);
  const value = draft.editLocale ?? null;
  // Declaring LABELS the text already typed — nothing moves, and this is the
  // only gesture that sets the program's author language.
  const onDeclare = (locale: Locale) =>
    setDraft((d) => ({ ...d, editLocale: locale, declaredAuthorLocale: locale }));
  // Switching carries every string into storage and reads the target language
  // back out, empty when there is nothing there.
  const onSwitch = (locale: Locale) =>
    setDraft((d) => changeEditLocale(d, locale));

  const options = supportedLngs.map((lng) => ({
    key: lng,
    label: LANGUAGE_ENDONYMS[lng],
    selected: lng === value,
  }));

  const trigger = (
    <Chip
      theme={theme}
      // Stands alone in the meta band with no neighbour to imply a
      // control, so it needs the findable outline (same reason as the gym
      // chip; the default border is the DS-37 defect).
      prominent
      style={styles.trigger}
      label={`${
        value
          ? LANGUAGE_ENDONYMS[value]
          : t("program.languageUndeclared", { defaultValue: "Language" })
      } ${disclosure.expanded ? "▴" : "▾"}`}
      expanded={disclosure.expanded}
      // ⛔ The value goes in the announcement. Without it a screen-reader
      // user hears the same words in every state and never learns WHICH
      // language is active — strictly less than the chip's own visible
      // label gives a sighted user. Same precedent as the gym chip.
      accessibilityLabel={`${t("program.languageA11y", {
        defaultValue: "Writing language",
      })}, ${
        value
          ? LANGUAGE_ENDONYMS[value]
          : t("program.languageUndeclared", { defaultValue: "Language" })
      }`}
      onPress={disclosure.toggle}
    />
  );

  const tray = disclosure.expanded ? (
    <ChoiceTray
      theme={theme}
      label={
        value
          ? t("program.languageSwitchQuestion", {
              defaultValue: "Which language are you writing in?",
            })
          : t("program.languageDeclareQuestion", {
              defaultValue: "Which language is this program written in?",
            })
      }
      options={options}
      onChoose={(key) => {
        disclosure.setExpanded(false);
        if (key === value) return;
        // ⛔ The two are not the same operation. Declaring only labels the
        // text that is already there; switching carries the draft into
        // another language and opens it EMPTY when nothing is stored.
        if (value === null) onDeclare(key as Locale);
        else onSwitch(key as Locale);
      }}
      footnote={
        value
          ? t("program.languageFootnote", {
              defaultValue:
                "Translations are optional. A program in one language is complete.",
            })
          : t("program.languageDeclareFootnote", {
              defaultValue: "Nothing gets translated: this only records the language.",
            })
      }
    />
  ) : null;

  return { trigger, tray };
}

// Same ceiling as the gym chip: an endonym like "Русский" must not push
// the meta band's label onto a second line.
const styles = { trigger: { maxWidth: 160 } } as const;
